import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { paymentApi } from '../services/api';
import { useToastActions } from '../components/ui/Toast';

interface PaymentVerifyResult {
  success: boolean;
  orderId?: string;
  customerId?: string;
  subscriptionId?: string;
  plan?: string;
  message?: string;
}

export function PaymentSuccessPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { success: toastSuccess, error: toastError } = useToastActions();
  
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [result, setResult] = useState<PaymentVerifyResult | null>(null);

  useEffect(() => {
    verifyPayment();
  }, []);

  const verifyPayment = async () => {
    try {
      // 提取 URL 参数
      const params = {
        // Checkout 相关参数
        checkout_id: searchParams.get('checkout_id') || searchParams.get('checkout'),
        session_id: searchParams.get('session_id'),
        
        // 订单相关参数
        order_id: searchParams.get('order_id') || searchParams.get('order'),
        order_number: searchParams.get('order_number'),
        
        // 客户相关参数
        customer_id: searchParams.get('customer_id') || searchParams.get('customer'),
        user_id: searchParams.get('user_id'),
        
        // 订阅相关参数
        subscription_id: searchParams.get('subscription_id') || searchParams.get('subscription'),
        
        // 产品/计划相关参数
        plan: searchParams.get('plan'),
        product_id: searchParams.get('product_id'),
        variant_id: searchParams.get('variant_id'),
        
        // 支付提供商（如果前端能检测到则传递，否则让后端根据配置自动检测）
        provider: searchParams.get('provider') || detectProvider() || undefined,
        
        // 签名参数（用于验证）
        signature: searchParams.get('signature') || searchParams.get('sig'),
        hash: searchParams.get('hash'),
        token: searchParams.get('token'),
        
        // 状态参数
        status: searchParams.get('status'),
        state: searchParams.get('state'),
        
        // 其他常见参数
        payment_intent: searchParams.get('payment_intent'),
        payment_intent_client_secret: searchParams.get('payment_intent_client_secret'),
        redirect_status: searchParams.get('redirect_status'),
      };

      // 过滤掉 null 值
      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(([_, v]) => v !== null && v !== undefined)
      ) as Record<string, string>;

      console.log('[PaymentSuccess] Verifying payment with params:', filteredParams);

      // 调用后端验证 API
      const response = await paymentApi.verifyPayment(filteredParams);
      
      if (response.data?.success) {
        setStatus('success');
        setResult(response.data);
        toastSuccess(t('payment.success'));
        
        // 支付成功后自动跳转到首页（延迟 2 秒让用户看到成功提示）
        setTimeout(() => {
          navigate('/home/store', { replace: true });
        }, 2000);
      } else {
        setStatus('error');
        setResult({
          success: false,
          message: response.data?.message || t('payment.verifyFailed')
        });
        toastError(response.data?.message || t('payment.verifyFailed'));
      }
    } catch (error: any) {
      console.error('[PaymentSuccess] Verification failed:', error);
      setStatus('error');
      setResult({
        success: false,
        message: error.response?.data?.message || t('payment.verifyError')
      });
      toastError(error.response?.data?.message || t('payment.verifyError'));
    }
  };

  // 根据 URL 参数和模式检测支付提供商
  // 返回空字符串表示让后端根据配置自动检测
  const detectProvider = (): string => {
    const url = window.location.href;
    
    // 1. 检查 URL 中是否有明确的提供商参数
    const providerParam = searchParams.get('provider');
    if (providerParam) return providerParam;
    
    // 2. 根据特定参数检测
    if (searchParams.get('payment_intent')) return 'stripe';
    if (searchParams.get('token')) return 'creem';
    if (searchParams.get('checkout_id') || searchParams.get('order_id')) {
      // LemonSqueezy 或 Paddle 都使用这些参数，需要进一步判断
      if (url.includes('lemonsqueezy') || url.includes('lsq')) return 'lemonsqueezy';
      if (url.includes('paddle')) return 'paddle';
      // 默认认为是 lemonsqueezy（如果无法确定）
      return '';
    }
    
    // 3. 根据 URL 模式检测
    if (url.includes('lemonsqueezy') || url.includes('lsq')) return 'lemonsqueezy';
    if (url.includes('stripe')) return 'stripe';
    if (url.includes('paddle')) return 'paddle';
    if (url.includes('creem')) return 'creem';
    if (url.includes('wechat')) return 'wechatpay';
    
    // 4. 无法确定，返回空字符串让后端根据配置自动检测
    return '';
  };

  const handleGoHome = () => {
    navigate('/home');
  };

  const handleGoToSubscription = () => {
    navigate('/subscription');
  };

  const handleRetry = () => {
    setStatus('verifying');
    verifyPayment();
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
      >
        {status === 'verifying' && (
          <>
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
            <h1 className="text-xl font-semibold text-zinc-900 mb-2">
              {t('payment.verifying') || '正在验证支付...'}
            </h1>
            <p className="text-sm text-zinc-500">
              {t('payment.pleaseWait') || '请稍候，我们正在确认您的支付状态'}
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-xl font-semibold text-zinc-900 mb-2">
              {t('payment.success') || '支付成功！'}
            </h1>
            <p className="text-sm text-zinc-500 mb-2">
              {t('payment.successMessage') || '感谢您的订阅，您的账户已升级'}
            </p>
            <p className="text-xs text-emerald-600 mb-6">
              {t('payment.autoRedirect') || '2秒后自动跳转到首页...'}
            </p>
            
            {result?.plan && (
              <div className="bg-zinc-50 rounded-lg p-4 mb-6 text-left">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-zinc-500">{t('payment.plan') || '计划'}</span>
                  <span className="font-medium text-zinc-900 capitalize">{result.plan}</span>
                </div>
                {result.orderId && (
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-zinc-500">{t('payment.orderId') || '订单号'}</span>
                    <span className="font-medium text-zinc-900">{result.orderId}</span>
                  </div>
                )}
                {result.customerId && (
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-500">{t('payment.customerId') || '客户 ID'}</span>
                    <span className="font-medium text-zinc-900">{result.customerId}</span>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleGoHome}
                className="flex-1 px-4 py-2 bg-zinc-100 text-zinc-700 rounded-lg hover:bg-zinc-200 transition-colors text-sm font-medium"
              >
                {t('common.backToHome') || '返回首页'}
              </button>
              <button
                onClick={handleGoToSubscription}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
              >
                {t('common.viewSubscription') || '查看订阅'}
              </button>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-xl font-semibold text-zinc-900 mb-2">
              {t('payment.failed') || '支付验证失败'}
            </h1>
            <p className="text-sm text-zinc-500 mb-6">
              {result?.message || t('payment.errorMessage') || '无法验证您的支付状态，请稍后重试或联系客服'}
            </p>

            <div className="flex gap-3">
              <button
                onClick={handleGoHome}
                className="flex-1 px-4 py-2 bg-zinc-100 text-zinc-700 rounded-lg hover:bg-zinc-200 transition-colors text-sm font-medium"
              >
                {t('common.backToHome') || '返回首页'}
              </button>
              <button
                onClick={handleRetry}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
              >
                {t('common.retry') || '重试'}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}

export default PaymentSuccessPage;
